import { useState, useEffect, useRef } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  Calendar, Clock, MapPin, Mic, Users, ArrowRight, Play, Download,
  Linkedin, Instagram, Twitter, Facebook, Globe, X, ChevronLeft, ChevronRight,
  Star, Award, CheckCircle2,
} from 'lucide-react'
import CinematicNav, { BackToTop } from '../../components/landing/CinematicNav'
import CinematicFooter from '../../components/landing/CinematicFooter'
import Reveal from '../../components/landing/Reveal'
import MasterclassSchema from '../../components/SEO/MasterclassSchema'
import MasterclassCountdown from '../../components/masterclass/MasterclassCountdown'
import ShareEvent from '../../components/masterclass/ShareEvent'
import SpeakerImage from '../../components/masterclass/SpeakerImage'
import { resolveImageUrl } from '../../utils/imageUrl'
import { masterclassService } from '../../services/masterclassService'
import { trackMasterclassView } from '../../services/websiteTracker'
import { SCHEDULE_SERIES_NAME } from '../../utils/masterclassSchedule'
import LoadingSpinner from '../../components/LoadingSpinner'

const ICONS = {
  check: CheckCircle2,
  mindset: Award,
  networking: Users,
  portfolio: Star,
  career: Award,
  ai: Star,
  business: Award,
  default: CheckCircle2,
}

function formatDate(d) {
  return d && !isNaN(new Date(d).getTime()) ? new Date(d).toLocaleDateString('en-UG', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : ''
}

function getYouTubeEmbed(url) {
  const match = url && url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([a-zA-Z0-9_-]{11})/)
  return match ? `https://www.youtube.com/embed/${match[1]}` : url
}

function extractYouTubeId(url) {
  const match = url && url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([a-zA-Z0-9_-]{11})/)
  return match ? match[1] : null
}

function Lightbox({ images, startIndex, onClose }) {
  const [index, setIndex] = useState(startIndex)
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight') setIndex((i) => (i + 1) % images.length)
      if (e.key === 'ArrowLeft') setIndex((i) => (i - 1 + images.length) % images.length)
    }
    window.addEventListener('keydown', handleKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', handleKey)
      document.body.style.overflow = ''
    }
  }, [images.length, onClose])

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" onClick={onClose}>
      <button className="absolute top-6 right-6 text-white/70 hover:text-white z-50" onClick={onClose}>
        <X className="w-8 h-8" />
      </button>
      {images.length > 1 && (
        <>
          <button className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white" onClick={(e) => { e.stopPropagation(); setIndex((i) => (i - 1 + images.length) % images.length) }}>
            <ChevronLeft className="w-10 h-10" />
          </button>
          <button className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white" onClick={(e) => { e.stopPropagation(); setIndex((i) => (i + 1) % images.length) }}>
            <ChevronRight className="w-10 h-10" />
          </button>
        </>
      )}
      <img
        src={resolveImageUrl(images[index].image_url)}
        alt={images[index].caption || ''}
        loading="lazy"
        className="max-h-[90vh] max-w-[90vw] object-contain"
        onClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => e.preventDefault()}
      />
      {images[index].caption && (
        <div className="absolute bottom-6 left-0 right-0 text-center text-white/80 font-sans text-sm px-4">
          {images[index].caption}
        </div>
      )}
    </div>
  )
}

export default function DetailPage() {
  const { slug } = useParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [scrolled, setScrolled] = useState(false)
  const [showBackToTop, setShowBackToTop] = useState(false)
  const [lightbox, setLightbox] = useState(null)
  const [activeVideo, setActiveVideo] = useState(null)
  const formRef = useRef(null)

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40)
      setShowBackToTop(window.scrollY > 800)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setLoading(true)
    masterclassService.getMasterclassDetail(slug)
      .then((res) => {
        setData(res)
        document.title = `${res.masterclass.topic} | ${SCHEDULE_SERIES_NAME}`
        if (res.masterclass?.id) {
          trackMasterclassView(res.masterclass.id, res.masterclass.topic, res.masterclass.speaker_name)
        }
      })
      .catch(() => {
        document.title = `Session Not Found | ${SCHEDULE_SERIES_NAME}`
      })
      .finally(() => setLoading(false))
  }, [slug])

  if (loading) return <LoadingSpinner className="cinema-page min-h-screen" />

  if (!data || !data.masterclass) {
    return (
      <div className="cinema-page min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="cinema-headline-lg text-white">Session not found</h1>
          <Link to="/masterclass/archive" className="cinema-btn-text mt-6 inline-flex">Browse archive</Link>
        </div>
      </div>
    )
  }

  const { masterclass, takeaways, gallery, videos, testimonials, resources, nextMasterclass } = data
  const speaker = {
    name: masterclass.speaker_name,
    title: masterclass.speaker_title,
    company: masterclass.speaker_company,
    bio: masterclass.speaker_bio,
    experience: masterclass.speaker_experience,
    photo: masterclass.speaker_photo,
    slug: masterclass.speaker_slug,
    specialization: masterclass.specialization,
    website: masterclass.website_url,
    linkedin: masterclass.linkedin_url,
    instagram: masterclass.instagram_url,
    twitter: masterclass.twitter_url,
    facebook: masterclass.facebook_url,
  }

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  return (
    <div className="cinema-page min-h-screen bg-cinema-black">
      <MasterclassSchema />
      <div className="cinema-grain" aria-hidden="true" />
      <CinematicNav scrolled={scrolled} navOverHero={!scrolled} />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        {masterclass.banner_url ? (
          <div className="absolute inset-0">
            <img src={resolveImageUrl(masterclass.banner_url)} alt={masterclass.topic} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-cinema-black via-cinema-black/70 to-cinema-black/40" />
          </div>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-b from-cinema-charcoal to-cinema-black" />
        )}
        <div className="cinema-glow-orb w-[600px] h-[600px] -top-40 -right-40 opacity-40" />

        <div className="relative z-10 w-full pt-40 pb-16 sm:pb-24">
          <div className="cinema-container">
            <div className="grid lg:grid-cols-2 gap-16 items-end">
              <div className="max-w-2xl">
                <Link to="/masterclass/archive" className="cinema-btn-text mb-6 inline-flex">
                  ← Archive
                </Link>
                <span className="cinema-label mb-4 block">{masterclass.category || 'General'}</span>
                <h1 className="cinema-headline-lg text-white">
                  {masterclass.topic}
                </h1>
                <div className="flex flex-wrap gap-6 mt-8 text-cinema-soft font-sans text-sm">
                  <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-cinema-gold" /> {formatDate(masterclass.event_date)}</span>
                  <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-cinema-gold" /> {masterclass.start_time || '5:00 PM'}</span>
                  <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-cinema-gold" /> {masterclass.venue || 'SMA, Kampala'}</span>
                  <span className="flex items-center gap-2"><Users className="w-4 h-4 text-cinema-gold" /> {masterclass.attendees_count || 0} attendees</span>
                </div>
                <div className="mt-6">
                  <ShareEvent slug={masterclass.slug || masterclass.id} title={masterclass.topic} summary={masterclass.description} />
                </div>
              </div>

              <div className="hidden lg:flex items-end justify-end">
                {(() => {
                  const hasPhoto = resolveImageUrl(speaker?.photo_url || speaker?.photo)
                  return hasPhoto && (
                    <div className="relative">
                      <div className="w-64 h-80 overflow-hidden">
                        <SpeakerImage speaker={speaker} loading="lazy" fit="card" />
                      </div>
                      <div className="absolute -bottom-4 -left-4 bg-cinema-glow text-white px-6 py-3 font-sans text-xs uppercase tracking-widest font-bold">
                        Speaker
                      </div>
                    </div>
                  )
                })()}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Speaker */}
      <section className="bg-cinema-charcoal py-24 sm:py-32">
        <div className="cinema-container">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <Reveal>
              <span className="cinema-label">About the Speaker</span>
              <h2 className="cinema-headline-lg mt-6 text-white">{speaker.name}</h2>
              <p className="text-cinema-gold font-sans text-lg mt-3">{speaker.title}</p>
              {speaker.company && <p className="text-cinema-muted font-sans mt-1">{speaker.company}</p>}
              {speaker.experience && (
                <div className="mt-6 inline-flex items-center gap-3 text-cinema-gold">
                  <Award className="w-5 h-5" />
                  <span className="text-white font-sans text-sm">{speaker.experience}</span>
                </div>
              )}
              {speaker.bio && (
                <p className="cinema-body mt-8 max-w-xl">{speaker.bio}</p>
              )}
              <div className="flex flex-wrap gap-3 mt-8">
                {speaker.linkedin && (
                  <a href={speaker.linkedin} target="_blank" rel="noreferrer" className="p-3 text-cinema-muted hover:text-cinema-gold transition-colors"><Linkedin className="w-4 h-4" /></a>
                )}
                {speaker.instagram && (
                  <a href={speaker.instagram} target="_blank" rel="noreferrer" className="p-3 text-cinema-muted hover:text-cinema-gold transition-colors"><Instagram className="w-4 h-4" /></a>
                )}
                {speaker.twitter && (
                  <a href={speaker.twitter} target="_blank" rel="noreferrer" className="p-3 text-cinema-muted hover:text-cinema-gold transition-colors"><Twitter className="w-4 h-4" /></a>
                )}
                {speaker.facebook && (
                  <a href={speaker.facebook} target="_blank" rel="noreferrer" className="p-3 text-cinema-muted hover:text-cinema-gold transition-colors"><Facebook className="w-4 h-4" /></a>
                )}
                {speaker.website && (
                  <a href={speaker.website} target="_blank" rel="noreferrer" className="p-3 text-cinema-muted hover:text-cinema-gold transition-colors"><Globe className="w-4 h-4" /></a>
                )}
              </div>
            </Reveal>
            <Reveal delay={150}>
              <div className="aspect-[3/4] max-w-md ml-auto overflow-hidden">
                {(() => {
                  const hasPhoto = resolveImageUrl(speaker?.photo_url || speaker?.photo)
                  return hasPhoto ? (
                    <SpeakerImage speaker={speaker} loading="lazy" fit="hero" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-cinema-graphite">
                      <Mic className="w-20 h-20 text-cinema-muted/20" />
                    </div>
                  )
                })()}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Summary */}
      <section className="bg-cinema-black py-24 sm:py-32">
        <div className="cinema-container max-w-4xl">
          <Reveal>
            <span className="cinema-label">Event Summary</span>
            <div className="cinema-divider mt-6 mb-12" />
            <div className="space-y-6 text-cinema-soft font-sans leading-relaxed text-lg">
              {(masterclass.recap || masterclass.description || 'A full recap of this session is coming soon.')
                .split('\n\n')
                .map((para, i) => <p key={i}>{para}</p>)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Takeaways */}
      {takeaways.length > 0 && (
        <section className="bg-cinema-charcoal py-24 sm:py-32">
          <div className="cinema-container">
            <Reveal>
              <span className="cinema-label">Key Takeaways</span>
              <h2 className="cinema-headline-lg mt-6 text-white">What You Learned</h2>
              <div className="cinema-divider mt-10 mb-16" />
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {takeaways.map((t, i) => {
                const Icon = ICONS[t.icon] || ICONS.default
                return (
                  <Reveal key={i} delay={i * 60}>
                    <div className="border-b border-white/5 p-6 group">
                      <div className="w-12 h-12 rounded-full bg-cinema-glow/10 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                        <Icon className="w-5 h-5 text-cinema-gold" />
                      </div>
                      <h3 className="font-display text-lg text-white mb-2">{t.label}</h3>
                      {t.description && <p className="text-cinema-muted font-sans text-sm">{t.description}</p>}
                    </div>
                  </Reveal>
                )
              })}
            </div>
          </div>
        </section>
      )}

      {/* Gallery */}
      {gallery.length > 0 && (
        <section className="bg-cinema-black py-24 sm:py-32">
          <div className="cinema-container">
            <Reveal>
              <span className="cinema-label">Photo Gallery</span>
              <h2 className="cinema-headline-lg mt-6 text-white">Session Moments</h2>
              <div className="cinema-divider mt-10 mb-16" />
            </Reveal>
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
              {gallery.map((img, i) => (
                <Reveal key={img.id} delay={i * 50}>
                  <button
                    onClick={() => setLightbox(i)}
                    className="block w-full text-left overflow-hidden group relative"
                  >
                    <img
                      src={resolveImageUrl(img.image_url)}
                      alt={img.caption || ''}
                      loading="lazy"
                      className="w-full transition-transform duration-700 group-hover:scale-105"
                    />
                    {img.caption && (
                      <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-white text-xs font-sans">{img.caption}</p>
                      </div>
                    )}
                  </button>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Videos */}
      {videos.length > 0 && (
        <section className="bg-cinema-charcoal py-24 sm:py-32">
          <div className="cinema-container">
            <Reveal>
              <span className="cinema-label">Video Highlights</span>
              <h2 className="cinema-headline-lg mt-6 text-white">Watch the Session</h2>
              <div className="cinema-divider mt-10 mb-16" />
            </Reveal>
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <div className="aspect-video bg-black overflow-hidden">
                  {activeVideo ? (
                    <iframe
                      src={getYouTubeEmbed(activeVideo.youtube_id || activeVideo.title)}
                      title="Video player"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-cinema-muted font-sans">
                      Select a video to watch
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-3 lg:max-h-[500px] overflow-y-auto pr-2">
                {videos.map((v, i) => {
                  const thumb = v.youtube_id ? `https://img.youtube.com/vi/${v.youtube_id}/mqdefault.jpg` : `https://img.youtube.com/vi/${extractYouTubeId(v.title)}/mqdefault.jpg`
                  const isActive = activeVideo?.id === v.id
                  return (
                    <button
                      key={v.id}
                      onClick={() => setActiveVideo(v)}
                      className={`w-full flex items-center gap-3 p-3 text-left transition-all ${isActive ? 'border-l-2 border-cinema-gold bg-white/5' : 'hover:bg-white/5'}`}
                    >
                      <div className="relative w-24 h-16 flex-shrink-0 overflow-hidden">
                        <img src={thumb} alt={v.title} loading="lazy" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <Play className="w-6 h-6 text-white fill-white" />
                        </div>
                      </div>
                      <div>
                        <p className={`font-sans text-sm font-medium line-clamp-2 ${isActive ? 'text-cinema-gold' : 'text-white'}`}>{v.title}</p>
                        <p className="text-cinema-muted text-xs mt-1">{v.category || 'Highlight'}</p>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Resources */}
      {resources.length > 0 && (
        <section className="bg-cinema-black py-24 sm:py-32">
          <div className="cinema-container max-w-4xl">
            <Reveal>
              <span className="cinema-label">Resources</span>
              <h2 className="cinema-headline-lg mt-6 text-white">Downloads</h2>
              <div className="cinema-divider mt-10 mb-16" />
            </Reveal>
            <div className="grid sm:grid-cols-2 gap-4">
              {resources.map((r, i) => (
                <Reveal key={r.id} delay={i * 60}>
                  <a
                    href={r.file_url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-4 p-5 border-b border-white/5 transition-all group"
                  >
                    <div className="w-12 h-12 bg-cinema-glow/10 flex items-center justify-center flex-shrink-0">
                      <Download className="w-5 h-5 text-cinema-gold" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-sans text-sm font-medium truncate">{r.title}</p>
                      <p className="text-cinema-muted text-xs uppercase mt-1">{r.resource_type} {r.file_size ? `· ${r.file_size}` : ''}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-cinema-muted group-hover:text-cinema-gold transition-colors" />
                  </a>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="bg-cinema-charcoal py-24 sm:py-32">
          <div className="cinema-container">
            <Reveal>
              <span className="cinema-label">Student Testimonials</span>
              <h2 className="cinema-headline-lg mt-6 text-white">What Students Said</h2>
              <div className="cinema-divider mt-10 mb-16" />
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <Reveal key={t.id} delay={i * 60}>
                  <div className="p-6 h-full flex flex-col border-b border-white/5">
                    <div className="flex gap-1 mb-4">
                      {[...Array(5)].map((_, idx) => (
                        <Star key={idx} className={`w-4 h-4 ${idx < t.rating ? 'text-cinema-gold fill-cinema-gold' : 'text-cinema-muted'}`} />
                      ))}
                    </div>
                    <p className="text-cinema-soft font-sans text-sm leading-relaxed flex-1">"{t.review}"</p>
                    <div className="flex items-center gap-3 mt-6 pt-6 border-t border-white/5">
                      <img src={resolveImageUrl(t.student_photo_url) || '/Tutors/default.jpg'} alt={t.student_name} loading="lazy" className="w-10 h-10 rounded-full object-cover" />
                      <div>
                        <p className="text-white font-sans text-sm font-medium">{t.student_name}</p>
                        <p className="text-cinema-muted text-xs">{t.course || 'Student'}</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Next Masterclass */}
      <section ref={formRef} className="bg-cinema-black py-24 sm:py-32 relative overflow-hidden">
        <div className="cinema-glow-orb w-[500px] h-[500px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-20" />
        <div className="cinema-container relative max-w-4xl text-center">
          <Reveal>
            <span className="cinema-label">Don't Miss the Next One</span>
            <h2 className="cinema-headline-lg mt-6 text-white">
              Register for the Next <span className="text-cinema-gold">Masterclass</span>
            </h2>
            <div className="mt-10 flex flex-col items-center gap-6">
              {nextMasterclass ? (
                <>
                  <p className="text-cinema-soft font-sans text-lg">{nextMasterclass.topic}</p>
                  <div className="max-w-sm mx-auto">
                    <MasterclassCountdown eventDate={nextMasterclass.event_date} />
                  </div>
                  <Link to="/masterclass" className="cinema-btn-primary group">
                    Register Now <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </>
              ) : (
                <>
                  <p className="text-cinema-muted font-sans">No upcoming session scheduled yet.</p>
                  <Link to="/masterclass/archive" className="cinema-btn-ghost">
                    Browse Archive
                  </Link>
                </>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      <CinematicFooter contactEmail="synergymultimediaacademy@gmail.com" />
      <BackToTop visible={showBackToTop} />

      {/* Sticky CTA */}
      <div className="fixed bottom-0 inset-x-0 z-40 px-4 pb-4 pt-10 bg-gradient-to-t from-cinema-black via-cinema-black/95 to-transparent sm:hidden">
        <Link to="/masterclass" className="cinema-btn-primary w-full group">
          Register for Next Masterclass <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </div>

      {/* Lightbox */}
      {lightbox !== null && <Lightbox images={gallery} startIndex={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  )
}
